import { useCallback, useEffect, useRef, useState } from "react";
import { emptyRequirements } from "../../../shared/labels/contracts";
import { calculatedLabelQuantity } from "../../../shared/labels/quantity-calculation";
import type {
  CommandMeta, DecisionInput, FileVersion, ListQuery, PlanPreview, PrintInput, RequestDetail, Role,
  RequestList, Requirements, SessionInfo,
} from "../../../shared/labels/contracts";
import { uiFault } from "./labels-api";
import type { LabelsApi } from "./labels-api";
import { imagePreview } from "./file-preview";
import { mutationSuccessFeedback } from "./label-feedback";
import { requestIdFromSearch, requestSelectionUrl } from "./request-selection";
import {
  approvedReview, canEditSales, defaultQueueForRoles, initialImportDraft, initialQuery, latestReview, queryForQueue, requirementsDiffer,
} from "./ui-types";
import type {
  DetailTab, DownloadedFile, Feedback, ImportDraft, LoadState, PrintDraft, QualityDraft, Queue,
  RequirementField, UiFault, UploadDraft, WorkspaceView,
} from "./ui-types";

type Confirmation = "discard" | "submit-sales" | "submit-review" | "decision" | "print" | "print-issue" | null;
type Mutation = {
  name: string;
  fingerprint: string;
  expectedVersion: number;
  successMessage: string;
  invoke: (meta: CommandMeta, signal: AbortSignal) => Promise<RequestDetail | null>;
  afterSuccess?: () => void;
};
type Attempt = { fingerprint: string; idempotencyKey: string; expectedVersion: number };
const initialUpload: UploadDraft = { file: null, kind: "artwork", changeNote: "" };
const initialQuality: QualityDraft = { decision: "approve", reason: "", correctionField: "Genel", correctionMessage: "" };
const initialPrint: PrintDraft = { quantity: "", declaration: false };
const copyRequirements = (value: Requirements): Requirements => ({ ...value, referenceFileIds: [...value.referenceFileIds] });

export function hasUnsavedLabelImport(view: WorkspaceView, draft: ImportDraft, preview: PlanPreview | null): boolean {
  return view === "import" && Boolean(draft.file || preview);
}

export function hasPendingUpload(upload: UploadDraft): boolean {
  return Boolean(upload.file || upload.changeNote.trim());
}

export function defaultReviewFileId(detail: RequestDetail): string {
  const newestRevision = detail.requirementsRevisions.slice().sort((a, b) => b.number - a.number)[0];
  return detail.files
    .filter((item) => item.kind === "artwork" && item.requirementsRevisionId === newestRevision?.id)
    .sort((a, b) => b.number - a.number)[0]?.id ?? "";
}

export function hasUnsavedLabelDetail(
  detail: RequestDetail | null,
  roles: readonly Role[],
  draft: Requirements,
  upload: UploadDraft,
  selectedFileId: string,
  qualityDraft: QualityDraft,
  printDraft: PrintDraft,
  printIssue: string,
): boolean {
  if (!detail) return false;
  if (canEditSales(roles, detail)) return requirementsDiffer(draft, detail.draft) || hasPendingUpload(upload);
  if (detail.state === "marketing" && roles.includes("marketing")) {
    return hasPendingUpload(upload) || selectedFileId !== defaultReviewFileId(detail);
  }
  if (detail.state === "quality_review" && roles.includes("quality")) {
    return qualityDraft.decision !== initialQuality.decision
      || Boolean(qualityDraft.reason.trim() || qualityDraft.correctionMessage.trim())
      || qualityDraft.correctionField !== initialQuality.correctionField;
  }
  if (detail.state === "print_ready" && roles.includes("printer")) return Boolean(printDraft.quantity || printDraft.declaration || printIssue.trim());
  return false;
}

export function useLabelWorkspace(api: LabelsApi, session: SessionInfo) {
  const scopeKey = JSON.stringify(session.actor);
  const generation = useRef(0);
  const mutationController = useRef<AbortController | null>(null);
  const fileController = useRef<AbortController | null>(null);
  const previewController = useRef<AbortController | null>(null);
  const attempt = useRef<Attempt | null>(null);
  const retryCommand = useRef<(() => void) | null>(null);
  const busyRef = useRef(false);
  const justMutated = useRef(false);
  const pendingNavigation = useRef<(() => void) | null>(null);
  const [view, setView] = useState<WorkspaceView>("requests");
  const [queue, setQueue] = useState<Queue>(() => defaultQueueForRoles(session.actor?.roles ?? []));
  const [query, setQuery] = useState<ListQuery>(() => queryForQueue(defaultQueueForRoles(session.actor?.roles ?? []), initialQuery));
  const [list, setList] = useState<RequestList | null>(null);
  const [listState, setListState] = useState<LoadState>("idle");
  const [listFault, setListFault] = useState<UiFault | null>(null);
  const [listReload, setListReload] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(() => typeof window === "undefined" ? null : requestIdFromSearch(window.location.search));
  const [detail, setDetail] = useState<RequestDetail | null>(null);
  const [detailState, setDetailState] = useState<LoadState>("idle");
  const [detailFault, setDetailFault] = useState<UiFault | null>(null);
  const [detailReload, setDetailReload] = useState(0);
  const [detailTab, setDetailTab] = useState<DetailTab>("work");
  const [draft, setDraft] = useState<Requirements>(() => copyRequirements(emptyRequirements));
  const [upload, setUpload] = useState<UploadDraft>(initialUpload);
  const [selectedFileId, setSelectedFileId] = useState("");
  const [qualityDraft, setQualityDraft] = useState<QualityDraft>(initialQuality);
  const [printDraft, setPrintDraft] = useState<PrintDraft>(initialPrint);
  const [printIssue, setPrintIssue] = useState("");
  const [assignment, setAssignment] = useState("");
  const [mutationBusy, setMutationBusy] = useState(false);
  const [mutationFault, setMutationFault] = useState<UiFault | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation>(null);
  const [downloaded, setDownloaded] = useState<DownloadedFile | null>(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [preview, setPreview] = useState<PlanPreview | null>(null);
  const [previewState, setPreviewState] = useState<LoadState>("idle");
  const [previewFault, setPreviewFault] = useState<UiFault | null>(null);
  const [importDraft, setImportDraft] = useState<ImportDraft>(initialImportDraft);
  const detailDirty = hasUnsavedLabelDetail(detail, session.actor?.roles ?? [], draft, upload, selectedFileId, qualityDraft, printDraft, printIssue);
  const importDirty = hasUnsavedLabelImport(view, importDraft, preview);
  const dirty = detailDirty || importDirty;
  const stale = mutationFault?.code === "VERSION_CONFLICT";
  const selectRequest = (id: string | null) => {
    setSelectedId(id);
    if (typeof window !== "undefined") window.history.replaceState(window.history.state, "", requestSelectionUrl(window.location.href, id));
  };

  const acceptDetail = useCallback((value: RequestDetail) => {
    setDetail(value);
    setDetailState("ready");
    setDetailFault(null);
    setDraft(copyRequirements(value.draft));
    setUpload(initialUpload);
    setQualityDraft(initialQuality);
    setPrintDraft(initialPrint);
    setPrintIssue("");
    setAssignment(value.salesUserId ?? "");
    setSelectedFileId(defaultReviewFileId(value));
  }, []);

  useEffect(() => {
    generation.current += 1;
    mutationController.current?.abort();
    fileController.current?.abort();
    previewController.current?.abort();
    attempt.current = null;
    retryCommand.current = null;
    busyRef.current = false;
    justMutated.current = false;
    setMutationBusy(false);
    setMutationFault(null);
    setFeedback(null);
    setSelectedId(typeof window === "undefined" ? null : requestIdFromSearch(window.location.search));
    setDetail(null);
    setDetailState("idle");
    setList(null);
    setListState("idle");
    setDownloaded(null);
    setView("requests");
    const firstQueue = defaultQueueForRoles(session.actor?.roles ?? []);
    setQueue(firstQueue);
    setQuery(queryForQueue(firstQueue, initialQuery));
    setPreview(null);
    setPreviewState("idle");
    setImportDraft(initialImportDraft);
    setConfirmation(null);
    return () => {
      generation.current += 1;
      mutationController.current?.abort();
      fileController.current?.abort();
      previewController.current?.abort();
    };
  }, [scopeKey, api, session.actor]);

  useEffect(() => {
    if (!session.actor) { setListState("unauthorized"); return; }
    const controller = new AbortController();
    const current = generation.current;
    setListState("loading");
    setListFault(null);
    const timer = setTimeout(() => {
      void api.list(query, controller.signal).then((value) => {
        if (controller.signal.aborted || generation.current !== current) return;
        setList(value);
        setListState("ready");
        justMutated.current = false;
      }).catch((error: unknown) => {
        if (controller.signal.aborted || generation.current !== current) return;
        const fault = uiFault(error);
        setListFault(fault);
        setListState(fault.status === 401 || fault.status === 403 ? "unauthorized" : "error");
        if (justMutated.current) setFeedback({ tone: "warning", message: "İşlem sunucuda kaydedildi; iş listesi yenilenemedi. Listeyi yeniden yükleyin." });
      });
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [api, query, listReload, scopeKey, session.actor]);

  useEffect(() => {
    fileController.current?.abort();
    setDownloaded(null);
    setFileLoading(false);
    setMutationFault(null);
    setDetailTab("work");
    attempt.current = null;
    retryCommand.current = null;
    if (!selectedId) { setDetail(null); setDetailState("idle"); return; }
    const current = generation.current;
    const controller = new AbortController();
    setDetail(null);
    setDetailState("loading");
    setDetailFault(null);
    void api.detail(selectedId, controller.signal).then((value) => {
      if (!controller.signal.aborted && current === generation.current) acceptDetail(value);
    }).catch((error: unknown) => {
      if (controller.signal.aborted || current !== generation.current) return;
      const fault = uiFault(error);
      setDetailFault(fault);
      setDetailState(fault.status === 401 || fault.status === 403 ? "unauthorized" : "error");
    });
    return () => controller.abort();
  }, [view, selectedId, detailReload, scopeKey, api, acceptDetail]);

  useEffect(() => () => {
    if (downloaded) URL.revokeObjectURL(downloaded.url);
  }, [downloaded]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const navigate = (operation: () => void) => {
    if (mutationBusy) return;
    if (dirty) { pendingNavigation.current = operation; setConfirmation("discard"); }
    else operation();
  };

  const runMutation = async (operation: Mutation): Promise<void> => {
    if (busyRef.current || stale) return;
    const current = generation.current;
    const existing = attempt.current?.fingerprint === operation.fingerprint ? attempt.current : null;
    const currentAttempt = existing ?? { fingerprint: operation.fingerprint, idempotencyKey: crypto.randomUUID(), expectedVersion: operation.expectedVersion };
    attempt.current = currentAttempt;
    retryCommand.current = () => { void runMutation(operation); };
    const controller = new AbortController();
    mutationController.current = controller;
    busyRef.current = true;
    setMutationBusy(true);
    setMutationFault(null);
    setFeedback(null);
    setConfirmation(null);
    try {
      const result = await operation.invoke({ idempotencyKey: currentAttempt.idempotencyKey, expectedVersion: currentAttempt.expectedVersion }, controller.signal);
      if (current !== generation.current || controller.signal.aborted) return;
      if (result) { acceptDetail(result); selectRequest(result.id); }
      else { setDetail(null); selectRequest(null); setPrintIssue(""); setPrintDraft(initialPrint); }
      setDownloaded(null);
      attempt.current = null;
      retryCommand.current = null;
      const outstanding = result?.notifications.filter((job) => ["awaiting_mapping", "failed", "delivery_unknown"].includes(job.status)) ?? [];
      const simulated = result?.notifications.some((job) => job.status === "simulated");
      setFeedback(mutationSuccessFeedback(operation.successMessage, outstanding.length, Boolean(simulated), session.demo));
      justMutated.current = true;
      setListReload((value) => value + 1);
      operation.afterSuccess?.();
    } catch (error: unknown) {
      if (current !== generation.current || controller.signal.aborted) return;
      const fault = uiFault(error);
      setMutationFault(fault);
      if (fault.status && fault.status < 500) { attempt.current = null; retryCommand.current = null; }
    } finally {
      if (current === generation.current) { busyRef.current = false; setMutationBusy(false); }
    }
  };

  const command = (name: string, data: unknown, message: string, invoke: Mutation["invoke"]) => {
    if (!detail) return;
    void runMutation({ name, fingerprint: detail.id + "|" + name + "|" + JSON.stringify(data), expectedVersion: detail.version, successMessage: message, invoke });
  };

  const changeRequirement = <K extends RequirementField>(field: K, value: Requirements[K]) => {
    setDraft((current) => {
      const next = { ...current, [field]: value };
      if (field === "workflowMode") {
        const recalculated = detail?.planRow ? calculatedLabelQuantity(detail.planRow, next.quantityBasis) : next.requestedPrintQuantity;
        return next.workflowMode === "new" ? { ...next, requestedPrintQuantity: recalculated, sourceFileId: null, preserveDimensions: false, widthMm: null, heightMm: null }
          : { ...next, requestedPrintQuantity: recalculated };
      }
      return field === "quantityBasis" && detail?.planRow
        ? { ...next, requestedPrintQuantity: calculatedLabelQuantity(detail.planRow, next.quantityBasis) }
        : next;
    });
    setMutationFault((current) => current?.code === "VERSION_CONFLICT" ? current : null);
  };

  const saveDraft = () => {
    if (!detail) return;
    const snapshot = copyRequirements(draft);
    const pendingUpload = upload;
    void runMutation({
      name: "save-draft", fingerprint: detail.id + "|save-draft|" + JSON.stringify(snapshot), expectedVersion: detail.version,
      successMessage: "Satış taslağı kaydedildi. Talebin aşaması değiştirilmedi.",
      invoke: (meta, signal) => api.saveDraft(detail.id, snapshot, meta, signal),
      afterSuccess: () => setUpload(pendingUpload),
    });
  };

  const validateSales = (): boolean => {
    const fields: Record<string, string> = {};
    const labels = { customerRequest: "Müşteri talebi", packaging: "Ambalaj gerekliliği", labelKind: "Etiket türü", widthMm: "En", heightMm: "Boy", texts: "Baskı metni", language: "Dil", requestedPrintQuantity: "İstenen etiket adedi" };
    const requiredFields = draft.workflowMode === null ? session.policy.requiredSalesFields
      : draft.workflowMode === "use_existing" ? ["packaging", "requestedPrintQuantity"] as const : ["customerRequest", "packaging", "requestedPrintQuantity"] as const;
    for (const field of requiredFields) {
      const value = draft[field];
      if (value === null || (typeof value === "string" && !value.trim())) fields[field] = "Göndermeden önce " + labels[field] + " alanını tamamlayın.";
    }
    if (draft.requestedPrintQuantity === null) fields.requestedPrintQuantity = "Plan birimi ve ürün ambalaj dönüşümüyle etiket adedini hesaplayın.";
    if (!draft.workflowMode) fields.workflowMode = "Etiket için yapılacak işlemi seçin.";
    if (draft.workflowMode && draft.workflowMode !== "new" && !draft.sourceFileId) fields.sourceFileId = "Mevcut etiket dosyasını seçin.";
    if (Object.keys(fields).length) { setMutationFault({ code: "VALIDATION", message: "Zorunlu satış alanlarını tamamlayın.", fields }); return false; }
    return true;
  };

  const uploadFile = () => {
    if (!detail || !upload.file) return;
    if (!session.policy.fileTypes.includes(upload.file.type) || upload.file.size > session.policy.maxFileBytes) {
      setMutationFault({ code: "FILE_INVALID", message: "Seçilen dosyanın türü veya boyutu izin verilen sınırlara uymuyor." });
      return;
    }
    const snapshot = { ...upload };
    command("upload-file", { name: upload.file.name, size: upload.file.size, type: upload.file.type, modified: upload.file.lastModified, kind: upload.kind, note: upload.changeNote }, "Yeni dosya sürümü sunucuda kaydedildi. Kaliteye henüz gönderilmedi.", (meta, signal) => api.upload(detail.id, snapshot, meta, signal));
  };

  const openFile = async (file: FileVersion) => {
    if (!detail || stale || fileLoading) return;
    const id = detail.id;
    const current = generation.current;
    fileController.current?.abort();
    const controller = new AbortController();
    fileController.current = controller;
    setFileLoading(true);
    setDownloaded(null);
    try {
      const blob = await api.file(id, file.id, controller.signal);
      const preview = await imagePreview(blob);
      if (!controller.signal.aborted && current === generation.current) setDownloaded({ url: URL.createObjectURL(blob), name: file.originalName, fileId: file.id, preview });
    } catch (error: unknown) {
      if (!controller.signal.aborted && current === generation.current) setMutationFault(uiFault(error));
    } finally {
      if (!controller.signal.aborted && current === generation.current) setFileLoading(false);
    }
  };

  const previewPlan = async () => {
    if (!importDraft.file || previewState === "loading" || mutationBusy) return;
    const controller = new AbortController();
    previewController.current?.abort();
    previewController.current = controller;
    const current = generation.current;
    setPreviewState("loading");
    setPreviewFault(null);
    setPreview(null);
    setImportDraft((value) => ({ ...value, selectedRowId: null, confirmed: false }));
    try {
      const result = await api.previewPlan(importDraft.file, importDraft.sheetName, crypto.randomUUID(), controller.signal);
      if (!controller.signal.aborted && current === generation.current) { setPreview(result); setPreviewState("ready"); }
    } catch (error: unknown) {
      if (!controller.signal.aborted && current === generation.current) { setPreviewFault(uiFault(error)); setPreviewState("error"); }
    }
  };

  const createRequest = () => {
    if (!preview || !importDraft.selectedRowId || !importDraft.confirmed) return;
    const selected = preview.rows.find((row) => row.id === importDraft.selectedRowId);
    if (!selected || selected.classification === "not_required") return;
    const due = importDraft.dueLocal ? new Date(importDraft.dueLocal) : null;
    if (due && !Number.isFinite(due.getTime())) { setPreviewFault({ code: "VALIDATION", message: "Hedef zamanı geçerli biçimde girin." }); return; }
    const input = { planRowId: selected.id, variantKey: importDraft.variantKey.trim(), labelKind: importDraft.labelKind.trim(), confirmed: true as const, salesUserId: importDraft.salesUserId || null, dueAt: due?.toISOString() ?? null };
    void runMutation({
      name: "create", fingerprint: "create|" + JSON.stringify(input), expectedVersion: 1, successMessage: "Teyit edilen etiket varyantı için talep sunucuda oluşturuldu.",
      invoke: (meta, signal) => api.create(input, meta.idempotencyKey, signal),
      afterSuccess: () => { setView("requests"); setQuery(initialQuery); setQueue("all"); setImportDraft((value) => ({ ...value, confirmed: false })); },
    });
  };

  const confirm = () => {
    if (confirmation === "discard") { const next = pendingNavigation.current; pendingNavigation.current = null; setConfirmation(null); next?.(); return; }
    if (!detail) return;
    if (confirmation === "submit-sales") command("submit-sales", detail.version, "Talep pazarlamaya gönderildi.", (meta, signal) => api.submitSales(detail.id, meta, signal));
    if (confirmation === "submit-review") command("submit-review", selectedFileId, "Belirli dosya ve gereklilik revizyonu kaliteye gönderildi.", (meta, signal) => api.submitReview(detail.id, selectedFileId, meta, signal));
    if (confirmation === "decision") {
      const review = latestReview(detail);
      if (!review) return;
      const input: DecisionInput = {
        reviewId: review.id, decision: qualityDraft.decision, reason: qualityDraft.reason.trim(),
        corrections: qualityDraft.decision === "reject" ? qualityDraft.correctionMessage.split(/\r?\n/u).map((message) => message.trim()).filter(Boolean).map((message) => ({ field: qualityDraft.correctionField.trim(), message })) : [],
      };
      command("quality-decision", input, input.decision === "reject" ? "Kalite gerekçesi kaydedildi; talep satışa geri döndü." : "İnceleme paketi için kalite onayı kaydedildi. Baskı hazırlığı sunucu tarafından değerlendirildi.", (meta, signal) => api.decide(detail.id, input, meta, signal));
    }
    if (confirmation === "print") {
      const review = approvedReview(detail);
      if (!review || !detail.approvedFileVersionId || !printDraft.declaration) return;
      const input: PrintInput = { reviewId: review.id, fileVersionId: detail.approvedFileVersionId, quantity: Number(printDraft.quantity), declaration: true };
      command("declare-print", input, "Baskı tamamlanması operatör beyanı olarak kaydedildi. Fiziksel yazıcı doğrulaması yapılmadı.", (meta, signal) => api.declarePrint(detail.id, input, meta, signal));
    }
    if (confirmation === "print-issue") {
      const reason = printIssue.trim();
      command("print-issue", reason, "Baskı sorunu kaydedildi; iş pazarlamaya gönderildi. Yeni dosya yeniden kalite onayından geçecek.", async (meta, signal) => { await api.reportPrintIssue(detail.id, reason, meta, signal); return null; });
    }
  };

  return {
    usePlanPreview: (value: PlanPreview) => navigate(() => {
      previewController.current?.abort(); attempt.current = null; retryCommand.current = null;
      setMutationFault(null); setFeedback(null); setPreview(value); setPreviewState("ready");
      setPreviewFault(null); setImportDraft({ ...initialImportDraft });
    }),
    view, queue, query, list, listState, listFault, selectedId, detail, detailState, detailFault, detailTab, draft, upload,
    selectedFileId, qualityDraft, printDraft, printIssue, setPrintIssue, assignment, mutationBusy, mutationFault, feedback, confirmation,
    downloaded, fileLoading, preview, previewState, previewFault, importDraft, dirty, pendingUpload: hasPendingUpload(upload), stale,
    setQuery: (next: ListQuery) => {
      if ((queue === "printer" && next.state !== "print_ready") || (queue === "completed" && next.state !== "print_completed") || (queue === "quality" && next.state !== "quality_review") || (queue === "marketing" && next.state !== "marketing") || (queue === "sales" && next.department !== "sales")) setQueue("all");
      setQuery(next);
    },
    setDetailTab, setUpload, setSelectedFileId, setQualityDraft, setPrintDraft, setAssignment, setImportDraft,
    changeRequirement, saveDraft, uploadFile, openFile: (file: FileVersion) => { void openFile(file); },
    previewPlan: () => { void previewPlan(); }, createRequest, confirm,
    setView: (next: WorkspaceView) => navigate(() => { setView(next); selectRequest(null); }),
    select: (id: string) => navigate(() => { selectRequest(id); setFeedback(null); }),
    closeDetail: () => navigate(() => { selectRequest(null); setFeedback(null); }),
    setQueue: (next: Queue) => navigate(() => { setQueue(next); setQuery((current) => queryForQueue(next, current)); selectRequest(null); }),
    clearFilters: () => {
      const firstQueue = defaultQueueForRoles(session.actor?.roles ?? []);
      setQuery(queryForQueue(firstQueue, initialQuery));
      setQueue(firstQueue);
    },
    retryList: () => setListReload((value) => value + 1),
    refreshDetail: () => navigate(() => { setDetailReload((value) => value + 1); }),
    cancelConfirmation: () => { setConfirmation(null); pendingNavigation.current = null; },
    confirmSales: () => {
      if (upload.file || upload.changeNote.trim()) { setMutationFault({ code: "UNSAVED_FILE", message: "Referans dosyası seçimini yükleyin veya temizleyin; ardından pazarlamaya gönderin." }); return; }
      if (detail && !requirementsDiffer(draft, detail.draft) && validateSales()) setConfirmation("submit-sales");
    },
    confirmReview: () => setConfirmation("submit-review"),
    confirmDecision: () => setConfirmation("decision"),
    confirmPrint: () => setConfirmation("print"),
    confirmPrintIssue: () => { if (printIssue.trim().length >= 3) setConfirmation("print-issue"); },
    reuseSourceFile: () => { if (detail) { const sourceFileId = detail.requirementsRevisions.at(-1)?.requirements.sourceFileId; if (sourceFileId) command("reuse-file", sourceFileId, "Mevcut etiket bu işin kalite incelemesine hazırlandı.", (meta, signal) => api.reuseFile(detail.id, sourceFileId, meta, signal)); } },
    retryMutation: () => retryCommand.current?.(),
    canRetryMutation: retryCommand.current !== null,
    assign: () => { if (detail && assignment) command("assign", assignment, "Satış sorumlusu sunucuda atandı.", (meta, signal) => api.assign(detail.id, assignment, meta, signal)); },
    retryNotification: (id: string) => { if (detail) command("notification-retry", id, "Bildirim işi yeniden değerlendirildi. İş aşaması değiştirilmedi.", (meta, signal) => api.retryNotification(detail.id, id, meta, signal)); },
  };
}
